<script setup lang="ts">
import route from "ziggy-js";
import useNumberFormat from "@/composables/useNumberFormat";
import { useTranslations } from "@/composables/useTranslations";

const { t } = useTranslations();
const { formatNumber } = useNumberFormat();

const columns = [
    {
        key: "domain",
        label: t("vue.dashboard_table_domain"),
        sortable: true,
        colClass: "w-[38%]",
    },
    {
        key: "emails",
        label: t("vue.dashboard_table_emails_reported"),
        sortable: true,
        colClass: "w-[16%]",
    },
    {
        key: "compliance",
        label: t("vue.dashboard_table_dmarc_compliance"),
        sortable: true,
        colClass: "w-[18%]",
    },
    {
        key: "policy",
        label: t("vue.dashboard_table_current_policy"),
        sortable: true,
        colClass: "w-[22%]",
    },
] as const;

const calculateCompliance = (pass: number, fail: number) =>
    pass + fail > 0 ? Math.floor((pass / (pass + fail)) * 100) + "%" : "0%";

const calculateColorByPercents = (percents: string) => {
    const n = parseInt(percents.replace("%", ""), 10);
    if (n > 75) return "#00c951";
    if (n > 50) return "#f0b100";
    return "#fb2c36";
};

const policy = (domain: any): string =>
    (domain?.dmarcPolicy ?? domain?.dmarc_policy ?? t("vue.none")) as string;

const props = withDefaults(
    defineProps<{
        activeDomains: any[];
        dateRange: { start: string; end: string };
        table: TableState;
        pagination: Pagination;
    }>(),
    {
        activeDomains: () => [],
    },
);
</script>

<template>
    <DynamicContentTable
        :title="t('vue.domain_overview')"
        :subtitle="t('vue.domain_overview_subheader')"
        :rows="activeDomains"
        :columns="columns"
        :endpoint="() => route('reports.index')"
        :base-query="{
            start_date: props.dateRange.start,
            end_date: props.dateRange.end,
        }"
        :table="table"
        :pagination="pagination"
        :row-link="(row) => route('reports.show', { domain: row.domain })"
        :search-placeholder="t('vue.search_domains')"
        :empty-text="t('vue.no_results') ?? 'No results'"
        :per-page-label="t('vue.per_page')"
    >
        <template #row="{ row }">
            <td class="px-6 py-4">
                <div class="flex items-center gap-3">
                    <div class="h-2 w-2 rounded-full bg-green-500"></div>

                    <span
                        class="font-medium text-blue-600 transition-colors duration-150 hover:text-blue-800"
                    >
                        {{ row.domain }}
                    </span>
                </div>
            </td>

            <template
                v-if="
                    (row?.report_current?.pass ?? 0) > 0 ||
                    (row?.report_current?.fail ?? 0) > 0
                "
            >
                <td class="px-6 py-4">
                    <span class="text-gray-900">
                        {{
                            formatNumber(
                                (row?.report_current?.pass ?? 0) +
                                    (row?.report_current?.fail ?? 0),
                            )
                        }}
                    </span>
                </td>

                <td class="px-6 py-4">
                    <div class="flex items-center gap-3">
                        <div class="max-w-24 flex-1">
                            <div class="h-2 w-full rounded-full bg-[#F97171]">
                                <div
                                    class="h-2 rounded-full bg-[#11B981] transition-all duration-300"
                                    :style="{
                                        width: calculateCompliance(
                                            row?.report_current?.pass ?? 0,
                                            row?.report_current?.fail ?? 0,
                                        ),
                                        color: calculateColorByPercents(
                                            calculateCompliance(
                                                row?.report_current?.pass ?? 0,
                                                row?.report_current?.fail ?? 0,
                                            ),
                                        ),
                                    }"
                                ></div>
                            </div>
                        </div>

                        <span class="text-sm font-medium text-green-600">
                            {{
                                calculateCompliance(
                                    row?.report_current?.pass ?? 0,
                                    row?.report_current?.fail ?? 0,
                                )
                            }}
                        </span>
                    </div>
                </td>

                <td class="px-6 py-4 text-left">
                    <span
                        v-if="policy(row) === 'reject'"
                        data-slot="badge"
                        class="policy-badge policy-reject w-fit inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-green-200 bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 transition-[color,box-shadow] hover:bg-green-100 focus-visible:ring-[3px] [&>svg]:pointer-events-none [&>svg]:size-3"
                        >{{ $t("vue.reject") }}</span
                    >

                    <span
                        v-else-if="policy(row) === 'quarantine'"
                        data-slot="badge"
                        class="policy-badge policy-quarantine w-fit inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-blue-200 bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 transition-[color,box-shadow] hover:bg-blue-100 focus-visible:ring-[3px] [&>svg]:pointer-events-none [&>svg]:size-3"
                    >
                        {{ $t("vue.quarantine") }}</span
                    >

                    <span
                        v-else
                        data-slot="badge"
                        class="policy-badge policy-none w-fit inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-transparent bg-secondary px-2 py-0.5 text-xs font-medium transition-[color,box-shadow] focus-visible:ring-[3px]"
                        >{{ $t("vue.none") }}</span
                    >
                </td>
            </template>

            <template v-else>
                <td colspan="3" class="px-6 py-4">
                    <div
                        class="flex items-center justify-center gap-2 py-2 text-gray-500"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-clock h-4 w-4"
                            aria-hidden="true"
                        >
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        <span class="text-sm">Waiting for reports</span>
                    </div>
                </td>
            </template>
        </template>
    </DynamicContentTable>
</template>
